import type {
  EntityFilterSchema,
  FilterDateRange,
  FilterEnumValue,
  FilterField,
  FilterFieldValue,
  FilterNumberRange,
  FilterState,
} from '@parcel/filters';
import { Popover, PopoverAnchor, PopoverContent, cn } from '@parcel/ui';
// `Filter` is lucide's funnel glyph (renamed `Funnel` in later lucide versions).
import { Filter, Search, X } from 'lucide-react';
import * as React from 'react';
import type { DynamicOptions } from './filter-menu.js';
import { fieldOptions, filterableFields } from './filter-menu.js';

const MAX_VALUE_PILLS = 2;

function formatDateLabel(iso: string): string {
  // Calendar date — always render in UTC or it shifts a day west of UTC.
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function dateRangeLabel(range: FilterDateRange): string {
  if (range.after && range.before) {
    return `${formatDateLabel(range.after)} – ${formatDateLabel(range.before)}`;
  }
  if (range.after) return `after ${formatDateLabel(range.after)}`;
  if (range.before) return `before ${formatDateLabel(range.before)}`;
  return '';
}

export function numberRangeLabel(range: FilterNumberRange, unit?: string): string {
  const fmt = (v: string) => Number(v).toLocaleString('en-US');
  const suffix = unit ? ` ${unit}` : '';
  if (range.min && range.max) return `${fmt(range.min)} – ${fmt(range.max)}${suffix}`;
  if (range.min) return `≥ ${fmt(range.min)}${suffix}`;
  if (range.max) return `≤ ${fmt(range.max)}${suffix}`;
  return '';
}

export function chipValueLabels(
  field: FilterField,
  value: FilterFieldValue,
  dynamicOptions: DynamicOptions = {},
): { value: string; label: string }[] {
  if (field.type === 'date-range') {
    const label = Array.isArray(value) ? '' : dateRangeLabel(value as FilterDateRange);
    return label ? [{ value: 'range', label }] : [];
  }
  if (field.type === 'number-range') {
    const label = Array.isArray(value)
      ? ''
      : numberRangeLabel(value as FilterNumberRange, field.unit);
    return label ? [{ value: 'range', label }] : [];
  }
  if (!Array.isArray(value)) return [];
  const options = fieldOptions(field, dynamicOptions);
  const byValue = new Map(options.map((o: FilterEnumValue) => [o.value, o.label]));
  return value.map((v) => ({ value: v, label: byValue.get(v) ?? v }));
}

interface FilterChipsProps {
  schema: Pick<EntityFilterSchema, 'fields'>;
  state: FilterState;
  dynamicOptions?: DynamicOptions;
  editingField: string | null;
  onEditField: (key: string | null) => void;
  onRemoveField: (key: string) => void;
  onRemoveSearch: () => void;
  onEditSearch: () => void;
  renderEditor: (field: FilterField) => React.ReactNode;
}

export function FilterChips({
  schema,
  state,
  dynamicOptions,
  editingField,
  onEditField,
  onRemoveField,
  onRemoveSearch,
  onEditSearch,
  renderEditor,
}: FilterChipsProps) {
  const fields = filterableFields(schema);
  const activeFields = Object.keys(state.fields)
    .map((key) => fields.find((f) => f.key === key))
    .filter((f): f is FilterField => Boolean(f));
  const q = state.q.trim();

  return (
    <>
      {q ? (
        <Chip
          icon={<Search className="h-3 w-3 text-muted-foreground" aria-hidden />}
          name="Search"
          values={[{ value: 'q', label: `“${q}”` }]}
          onName={onEditSearch}
          onRemove={onRemoveSearch}
        />
      ) : null}
      {activeFields.map((field) => {
        const value = state.fields[field.key];
        if (value == null) return null;
        // The chip anchor lives outside the portaled popover content, so Radix
        // treats pointerdowns/focus on it as "outside" and would auto-close the
        // editor before the chip's own click handler runs (close→reopen
        // flicker). Swallow dismissal for interactions landing on this chip;
        // the name button then acts as a real toggle and ✕ removes the field
        // (unmounting chip and popover together).
        const guardOwnChipDismiss = (event: {
          target: EventTarget | null;
          preventDefault: () => void;
        }) => {
          const target = event.target as Element | null;
          if (target?.closest('[data-chip-key]')?.getAttribute('data-chip-key') === field.key) {
            event.preventDefault();
          }
        };
        return (
          <Popover
            key={field.key}
            open={editingField === field.key}
            onOpenChange={(open) => onEditField(open ? field.key : null)}
          >
            <PopoverAnchor asChild>
              <Chip
                data-chip-key={field.key}
                icon={<Filter className="h-3 w-3 text-muted-foreground" aria-hidden />}
                name={field.label}
                values={chipValueLabels(field, value, dynamicOptions)}
                onName={() => onEditField(editingField === field.key ? null : field.key)}
                onRemove={() => onRemoveField(field.key)}
              />
            </PopoverAnchor>
            <PopoverContent
              className="w-[260px] p-0"
              align="start"
              onPointerDownOutside={guardOwnChipDismiss}
              onFocusOutside={guardOwnChipDismiss}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                const chip = document.querySelector(`[data-chip-key="${field.key}"]`);
                chip?.querySelector('button')?.focus();
              }}
            >
              {renderEditor(field)}
            </PopoverContent>
          </Popover>
        );
      })}
    </>
  );
}

export const Chip = React.forwardRef<
  HTMLDivElement,
  {
    icon: React.ReactNode;
    name: string;
    /** A plain operator word between the name and the values ("is not", "is empty"). */
    operator?: string;
    values: { value: string; label: string }[];
    onName: () => void;
    onRemove: () => void;
  } & React.HTMLAttributes<HTMLDivElement>
>(({ icon, name, operator, values, onName, onRemove, className, ...props }, ref) => {
  const shown = values.slice(0, MAX_VALUE_PILLS);
  const extra = values.length - shown.length;
  return (
    <div
      ref={ref}
      className={cn(
        'group/chip inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-muted py-1 pl-2.5 pr-1.5 text-xs text-foreground',
        className,
      )}
      {...props}
    >
      <span className="inline-flex group-hover/chip:hidden group-focus-within/chip:hidden">
        {icon}
      </span>
      <button
        type="button"
        aria-label={`Remove ${name} filter`}
        onClick={onRemove}
        className="hidden items-center group-hover/chip:inline-flex group-focus-within/chip:inline-flex"
      >
        <X className="h-3 w-3 text-muted-foreground" aria-hidden />
      </button>
      <button
        type="button"
        aria-label={`Edit ${name} filter`}
        onClick={onName}
        className="font-medium group-hover/chip:underline"
      >
        {name}
      </button>
      {operator ? <span className="text-muted-foreground">{operator}</span> : null}
      {shown.map(({ value, label }) => (
        <span
          key={value}
          // ph-no-capture on the value, not the chip container: the container
          // also holds the field label and icon, which are useful and not PII.
          className="ph-no-capture rounded-full bg-background px-2 py-0.5 shadow-[0_0_0_1px_hsl(var(--border))]"
        >
          {label}
        </span>
      ))}
      {extra > 0 ? (
        <span className="rounded-full bg-background px-2 py-0.5 shadow-[0_0_0_1px_hsl(var(--border))] text-muted-foreground">
          +{extra}
        </span>
      ) : null}
    </div>
  );
});
Chip.displayName = 'Chip';
